import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Button } from '@mui/material';

import { IBatch, IBatchShipment } from '../../types/shipment';

import { AppConfig } from '../../config/appConfig';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';

import "../../styles/entity/batchList.scss";

export interface IBatchTableProps {
	data?: IBatch[];
	payee?: string;
	customer?: string;
	status?: string;
}

const BatchTable: React.FunctionComponent<IBatchTableProps> = ({ data, payee, customer, status }) => {
	const [dataView, setDataView] = useState<IBatch[]>([]);
	const [firstVisibleRow, setFirstVisibleRow] = useState(0);
	const [lastVisibleRow, setLastVisibleRow] = useState(9);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.batchListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoadingBatches, setIsLoadingBatches] = useState(false);

	useEffect(() => {
		if (data) {
			setIsLoadingBatches(true);
			let newData = data.slice();

			if (sortField) {
				switch (sortField) {
					case "batch": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.id, b.id));
						break;
					}
					case "invoice": {
						newData.sort((a, b) => UtilityService.compareStrings(a.invoiceNumber, b.invoiceNumber));
						break;
					}
					case "payee": {
						newData.sort((a, b) => UtilityService.compareStrings(a.payeeName, b.payeeName));
						break;
					}
					case "batchSize": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.shipments.length, b.shipments.length));
						break;
					}
					case "status": {
						newData.sort((a, b) => UtilityService.compareStrings(a.status, b.status));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareNumbers(a.id, b.id));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
			setFirstVisibleRow(0);
			setLastVisibleRow(pageSize - 1);
			setIsLoadingBatches(false);
		}
	}, [data, sortField, sortAscending])
	
	useEffect(() => {
		setPage(0);
	}, [payee, customer, status]);

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	const formatBolsColumn = (shipments: IBatchShipment[]) => {
		if (!shipments || shipments.length == 0) return <span className="empty">Batch is empty</span>;

		const displayMax = 4;
		let displayString = '';
		for (let i = 0; i < displayMax; i++) {
			if (shipments[i]) {
				displayString += shipments[i].bolNumber.trim();

				if (shipments[i + 1]) {
					displayString += ', ';

					if (i == displayMax - 1) {
						displayString += '...';
						break;
					}
				}
			}
		}

		return displayString
	}

	const formatMonthColumn = (month: string) => {
		if (!month || month.length != 6) return "N/A";
		return month.substring(0, 4) + '-' + month.substring(4);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-batch">
					<p onClick={() => handleHeaderClick("batch")}>
						Batch #
						{" "}
						{(sortField == "batch" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "batch" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-invoice">
					<p onClick={() => handleHeaderClick("invoice")}>
						Invoice #
						{" "}
						{(sortField == "invoice" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "invoice" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-month">
					<p onClick={() => handleHeaderClick("month")}>
						Month
						{" "}
						{(sortField == "month" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "month" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-payee">
					<p onClick={() => handleHeaderClick("payee")}>
						Payee
						{" "}
						{(sortField == "payee" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "payee" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-batchSize">
					<p onClick={() => handleHeaderClick("batchSize")}>
						Batch Size
						{" "}
						{(sortField == "batchSize" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "batchSize" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-bols">
					<p>
						BOLs
					</p>
				</div>
				<div className="col-customer">
					<p>
						Customer
					</p>
				</div>
				<div className="col-status">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<Spinner dependency={isLoadingBatches} text="Loading Batches...">
				<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
								<div className="row" key={item.id}>
									<div className="col-batch"><p>{item.id}</p></div>
									<div className="col-invoice"><p>{item.invoiceNumber}</p></div>
									<div className="col-month"><p>{formatMonthColumn(item.month)}</p></div>
									<div className="col-payee"><p>{item.payeeName ?? "N/A"} ({item.payeeType.substring(0, 1)})</p></div>
									<div className="col-batchSize"><p>{item.shipments?.length}</p></div>
									<div className="col-bols"><p>{formatBolsColumn(item.shipments)}</p></div>
									<div className="col-customer"><p>{item.customers?.length == 1 ? item.customers[0].name : item.customers?.length > 1 ? `Multiple (${item.customers?.length})` : <span className="empty">None</span>}</p></div>
									<div className="col-status"><p>{item.status}</p></div>
									<div className="col-controls">
										<p>
											<NavLink to={"/batch/" + item.id}>
												<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
												{" "}
												Open
											</NavLink>
										</p>
									</div>
								</div>
							))
						) : (<div className="no-results">0 results</div>)}
				</div>
				{dataView?.length > 0 &&
						<div className="pager">
							<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
							<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
							<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
						</div>
				}
			</Spinner>
		</div>
	)
}

export default BatchTable;